<template>
  <div class="phonebot-container">
    <!-- ✅ HEADER MIT DELETE BUTTON AUSSERHALB DER TABS -->
    <div class="phonebot-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-1">
            <i class="fas fa-robot me-2 text-primary"></i>
            Phonebot Konfiguration
          </h4>
          <p class="text-muted mb-0">
            {{ vbot.bezeichnung || 'Unbenannter Phonebot' }}
            <span v-if="vbot.license_Key" class="badge bg-light text-dark ms-2">
              {{ vbot.license_Key }}
            </span>
          </p>
        </div>
        
        <!-- DELETE BUTTON IM HEADER -->
        <div class="header-actions">
          <button 
            class="btn btn-outline-danger" 
            type="button" 
            @click="deleteItem(vbot.id)"
            title="Phonebot löschen"
          >
            <i class="fas fa-trash me-2"></i>
            Phonebot löschen
          </button>
        </div>
      </div>
    </div>

    <!-- Hauptformular -->
    <div class="p-4">
      <Form role="form" @submit="handleUpdate">
        
        <!-- ✅ CUSTOM TAB NAVIGATION (Vue-basiert) -->
        <ul class="nav nav-tabs" role="tablist">
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'general' }"
              @click.prevent="setActiveTab('general')"
              href="#"
            >
              Allgemein
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'abo-info' }"
              @click.prevent="setActiveTab('abo-info')"
              href="#"
            >
              Abo-Info
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'sip' }"
              @click.prevent="setActiveTab('sip')"
              href="#"
            >
              SIP-Einstellungen
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'ai' }"
              @click.prevent="setActiveTab('ai')"
              href="#"
            >
              KI-Modelle
            </a>
          </li>
          <li class="nav-item" v-if="role === 'super'">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'translation' }"
              @click.prevent="setActiveTab('translation')"
              href="#"
            >
              Dolmetscher
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'network' }"
              @click.prevent="setActiveTab('network')"
              href="#"
            >
              Netzwerk
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'email' }"
              @click.prevent="setActiveTab('email')"
              href="#"
            >
              E-Mail
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'advanced' }"
              @click.prevent="setActiveTab('advanced')"
              href="#"
            >
              Erweitert
            </a>
          </li>
        </ul>

        <!-- ✅ TAB CONTENT (Vue-basiert) -->
        <div class="tab-content mt-4">
          
          <!-- ALLGEMEIN TAB -->
          <div v-if="activeTab === 'general'" class="tab-pane active">
            <h5 class="mb-3">📋 Allgemeine Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Bezeichnung
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Bezeichnung</strong>
                        Der eindeutige Name für Ihren Phonebot.
                      </div>
                    </span>
                  </label>
                  <Field type="text" class="form-control" v-model="vbot.bezeichnung" name="bezeichnung" id="bezeichnung" />
                  <ErrorMessage name="bezeichnung" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Product Name
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Produktversion</strong>
                        Basic: Grundfunktionen | Advanced: Erweiterte Features
                      </div>
                    </span>
                  </label>
                  <Field as="select" class="form-control" v-model="vbot.product_name" name="product_name" id="product_name">
                    <option value="" disabled>Select Product Name</option>
                    <option value="KI-Phone-Basic">KI-Phone-Basic</option>
                    <option value="KI-Phone-Advanced">KI-Phone-Advanced</option>
                  </Field>
                  <ErrorMessage name="product_name" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Ansagetext
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Begrüßungstext</strong>
                        Der erste Text, den Anrufer hören.
                      </div>
                    </span>
                  </label>
                  <textarea class="form-control" v-model="vbot.ansagetext" name="ansagetext" id="ansagetext" rows="3"></textarea>
                  <ErrorMessage name="ansagetext" class="text-danger" />
                </div>
              </div>
            </div>
            
            <!-- ✅ ENHANCED PROMPT OVERLAY SEKTION -->
            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4" v-if="!showPromptOverlay">
                  <label>Prompt</label>
                  <textarea
                    class="form-control prompt-trigger"
                    v-model="vbot.prompt"
                    name="prompt"
                    id="prompt"
                    rows="8"
                    @click="openPromptOverlay"
                    @focus="openPromptOverlay"
                    placeholder="Klicken zum Erweitern..."
                    readonly
                    style="cursor: pointer;"
                  ></textarea>
                  <small class="text-muted">💡 Klicken Sie hier, um den erweiterten Editor zu öffnen</small>
                </div>

                <!-- ✅ ENHANCED VOLLBILD-OVERLAY -->
                <div v-if="showPromptOverlay" class="prompt-fullscreen-overlay">
                  <div class="prompt-fullscreen-container">
                    <!-- Header -->
                    <div class="prompt-header">
                      <h4 class="mb-0">
                        <i class="fas fa-edit me-2"></i>
                        Prompt Editor
                      </h4>
                      <div class="header-actions d-flex align-items-center">
                        <!-- ✅ AI GENERATION BUTTON -->
                        <button 
                          type="button" 
                          class="btn btn-sm btn-outline-light me-2"
                          @click="toggleAiMode"
                          :title="showAiMode ? 'Einzelansicht' : 'KI-Assistent aktivieren'"
                        >
                          <i :class="showAiMode ? 'fas fa-columns' : 'fas fa-magic'"></i>
                        </button>
                        
                        <button 
                          type="button" 
                          class="btn btn-sm btn-outline-light me-2"
                          @click="closePromptOverlay"
                          title="Minimieren"
                        >
                          <i class="fas fa-minus"></i>
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-sm btn-light"
                          @click="closePromptOverlay"
                          title="Schließen"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <!-- ✅ ENHANCED CONTENT -->
                    <div class="prompt-content">
                      <!-- ✅ SINGLE VIEW (Normal) -->
                      <div v-if="!showAiMode" class="single-view">
                        <textarea
                          class="form-control prompt-fullscreen-textarea"
                          v-model="vbot.prompt"
                          name="prompt"
                          ref="fullscreenPrompt"
                          placeholder="Geben Sie hier Ihren Prompt ein..."
                        ></textarea>
                      </div>

                      <!-- ✅ DUAL VIEW (AI-Modus) -->
                      <div v-else class="dual-view">
                        <div class="row h-100">
                          <!-- Original Prompt (Links) -->
                          <div class="col-md-6 prompt-column">
                            <div class="prompt-section h-100">
                              <h6 class="prompt-section-title">
                                <i class="fas fa-edit me-2"></i>
                                Prompt
                              </h6>
                              <textarea
                                class="form-control prompt-section-textarea"
                                v-model="vbot.prompt"
                                name="original_prompt"
                                placeholder="Geben Sie hier Ihren Prompt ein..."
                              ></textarea>
                            </div>
                          </div>

                          <!-- Generierter Prompt (Rechts) -->
                          <div class="col-md-6 prompt-column">
                            <div class="prompt-section h-100">
                              <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="prompt-section-title mb-0">
                                  <i class="fas fa-robot me-2 text-primary"></i>
                                  Generierter Prompt
                                </h6>
                                <!-- ✅ FUTURE: Generate Button -->
                                <button 
                                  type="button" 
                                  class="btn btn-sm btn-primary"
                                  @click="generatePrompt"
                                  :disabled="isGenerating"
                                  title="Prompt generieren"
                                >
                                  <i :class="isGenerating ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'"></i>
                                  {{ isGenerating ? 'Generiert...' : 'Generieren' }}
                                </button>
                              </div>
                              <textarea
                                class="form-control prompt-section-textarea generated-prompt"
                                v-model="generatedPrompt"
                                name="generated_prompt"
                                placeholder="Hier erscheint der generierte Prompt..."
                                readonly
                              ></textarea>
                              
                              <!-- ✅ FUTURE: Copy/Apply Buttons -->
                              <div class="mt-2 d-flex gap-2" v-if="generatedPrompt">
                                <button 
                                  type="button" 
                                  class="btn btn-sm btn-outline-secondary"
                                  @click="copyGeneratedPrompt"
                                  title="Generierten Prompt kopieren"
                                >
                                  <i class="fas fa-copy me-1"></i>
                                  Kopieren
                                </button>
                                <button 
                                  type="button" 
                                  class="btn btn-sm btn-success"
                                  @click="applyGeneratedPrompt"
                                  title="Generierten Prompt übernehmen"
                                >
                                  <i class="fas fa-arrow-left me-1"></i>
                                  Übernehmen
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Footer -->
                    <div class="prompt-footer">
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="text-muted small">
                          <span v-if="!showAiMode">
                            Zeichen: {{ (vbot.prompt || '').length }} | ESC zum Schließen
                          </span>
                          <span v-else>
                            Original: {{ (vbot.prompt || '').length }} Zeichen | 
                            Generiert: {{ (generatedPrompt || '').length }} Zeichen | 
                            ESC zum Schließen
                          </span>
                        </div>
                        <div>
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary me-2"
                            @click="closePromptOverlay"
                          >
                            Schließen
                          </button>
                          <button 
                            type="button" 
                            class="btn btn-success"
                            @click="saveAndClose"
                          >
                            <i class="fas fa-save me-1"></i>
                            Fertig
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Backdrop -->
                <div 
                  v-if="showPromptOverlay" 
                  class="prompt-backdrop"
                  @click="closePromptOverlay"
                ></div>
              </div>
            </div>
          </div>

          <!-- ✅ ABO-INFO TAB (OHNE DELETE BUTTON) -->
          <div v-if="activeTab === 'abo-info'" class="tab-pane active">
            <h5 class="mb-3">💳 Abonnement Information</h5>
            
            <div class="row">
              <div class="col-md-12">
                <div class="card border-0 shadow-sm">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="fas fa-crown me-2 text-warning"></i>
                      Abonnement Status
                    </h5>
                  </div>
                  
                  <div class="row">
                    <div class="col-md-12">
                      <label>Status:</label>
                      <span
                        :class="statusClass"
                        class="badge px-3 py-2"
                      >
                        {{ statusText }}
                      </span>
                    </div>
                  </div>
                  <div class="row">
                    <div class="checkout-container"></div>
                    <button type="button" @click="openCheckout" v-if="vbot.payment_status === 0">Checkout Paddle öffnen</button>
                </div>    
                </div>

                
              </div>
            </div>
          </div>

          <!-- SIP TAB -->
          <div v-if="activeTab === 'sip'" class="tab-pane active">
            <h5 class="mb-3">📞 SIP-Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Benutzername</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_benutzername" name="sip_benutzername" id="sip_benutzername" />
                  <ErrorMessage name="sip_benutzername" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Password</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_passwd" name="sip_passwd" id="sip_passwd" />
                  <ErrorMessage name="sip_passwd" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Registername</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_registername" name="sip_registername" id="sip_registername" />
                  <ErrorMessage name="sip_registername" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Telefonnummer</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_phonenumber" name="sip_phonenumber" id="sip_phonenumber" />
                  <ErrorMessage name="sip_phonenumber" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Server</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_server" name="sip_server" id="sip_server" />
                  <ErrorMessage name="sip_server" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Port</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_port" name="sip_port" id="sip_port" />
                  <ErrorMessage name="sip_port" class="text-danger" />
                </div>
              </div>
            </div>

            <!-- SIP-Domain: sichtbar für Admin-Rollen (admin, adminuser, super) -->
            <div class="row" v-if="isAdminRole">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>
                    SIP Domain
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>SIP Domain</strong>
                        Einstellung für Innovaphone. Wird an den Phonebot übermittelt und legt die SIP-Domain fest, gegen die sich der Client registriert.
                      </div>
                    </span>
                  </label>
                  <Field type="text" class="form-control" v-model="vbot.sip_domain" name="sip_domain" id="sip_domain" />
                  <ErrorMessage name="sip_domain" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="row">
            <div class="col-md-6">
              <div class="input-group input-group-static mb-4">
                <label>
                  SIP Registrierungsintervall (Sekunden)
                  <span class="info-icon">
                    i
                    <div class="info-tooltip">
                      <strong>Registrierungsintervall</strong>
                      Zeitspanne, nach der sich der SIP-Client automatisch neu beim Server anmeldet, um die Verbindung aktiv zu halten.
                    </div>
                  </span>
                </label>
                <Field
                  type="text"
                  class="form-control"
                  v-model="vbot.sip_registrierungsinterval"
                  name="sip_registrierungsinterval"
                  id="sip_registrierungsinterval"
                />
                <ErrorMessage name="sip_registrierungsinterval" class="text-danger" />
              </div>
            </div>
            
          </div>

            <!-- Transport Type und weitere SIP Einstellungen -->
            <div class="row">
              
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SDK</label>
                  <Field as="select" class="form-control" v-model="vbot.sdk" name="sdk" id="sdk">
                    <option value="" disabled>Select SDK</option>
                    <option value="PortSIP">PortSIP</option>
                    <option value="Ozeki">Ozeki</option>
                  </Field>
                  <ErrorMessage name="sdk" class="text-danger" />
                </div>
              </div>
            </div>
            

            <!-- Checkboxes für SIP -->
            
          </div>

          <!-- EMAIL TAB -->
          <div v-if="activeTab === 'email'" class="tab-pane active">
            <h5 class="mb-3">📧 E-Mail-Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email Server</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_server" name="e_mail_server" id="e_mail_server" />
                  <ErrorMessage name="e_mail_server" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email Server Port</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_server_port" name="e_mail_server_port" id="e_mail_server_port" />
                  <ErrorMessage name="e_mail_server_port" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email User</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_user" name="e_mail_user" id="e_mail_user" />
                  <ErrorMessage name="e_mail_user" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email Password</label>
                  <Field type="password" class="form-control" v-model="vbot.e_mail_passwd" name="e_mail_passwd" id="e_mail_passwd" />
                  <ErrorMessage name="e_mail_passwd" class="text-danger" />
                </div>
              </div>
              
            </div>
            
            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>Email Server From</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_server_from" name="e_mail_server_from" id="e_mail_server_from" />
                  <ErrorMessage name="e_mail_server_from" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="mail_certifikate_validation">Mail Certificate Validation</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.mail_certifikate_validation"
                      name="mail_certifikate_validation"
                      id="mail_certifikate_validation"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI TAB -->
          <div v-if="activeTab === 'ai'" class="tab-pane active">
            <h5 class="mb-3">🤖 KI-Modelle & Einstellungen</h5>
            
            <!-- Realtime-Anbieter (nur Superadmin): bei Azure erscheinen weiter unten die Felder für Azure Voice Live.
                 Feldname bei AI-Phone: realtimeProvider -->
            <div class="row" v-if="role === 'super'">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Realtime-Anbieter
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Realtime-Anbieter</strong>
                        Wer die Realtime-Session bereitstellt. Bei Azure erscheinen weiter unten die Felder für Azure Voice Live. Mit EU-Datenverarbeitung immer Azure.
                      </div>
                    </span>
                  </label>
                  <Field as="select" class="form-control" v-model="vbot.realtime_provider" name="realtime_provider" id="realtime_provider" :disabled="vbot.eu_data_processing">
                    <option value="">OpenAI (Standard)</option>
                    <option value="azure">Azure</option>
                  </Field>
                  <small class="form-text text-muted w-100" v-if="vbot.eu_data_processing">
                    Fest auf Azure, solange EU-Datenverarbeitung an ist.
                  </small>
                  <ErrorMessage name="realtime_provider" class="text-danger" />
                </div>
              </div>
            </div>

            <!-- Chat Model Selection -->
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Chat Model</label>
                  <Field as="select" class="form-control" v-model="vbot.chatmodel" name="chatmodel" id="chatmodel">
                    <option value="gpt-4o" selected>gpt-4o</option>
                    <option value="gpt-5">gpt-5</option>
                    <option value="gpt-5-mini">gpt-5-mini</option>
                    <option value="gpt-5-nano">gpt-5-nano</option>
                    <option value="gpt-4.1">gpt-4.1</option>
                    <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                    <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                    <option value="gpt-4o-mini">gpt-4o-mini</option>
                    <option value="gpt-4-turbo">gpt-4-turbo</option>
                    <option value="gpt-4">gpt-4</option>
                    <option value="gpt-3.5-turbo">gpt-3.5-turbo</option>
                    <option value="o1">o1</option>
                    <option value="o1-mini">o1-mini</option>
                    <option value="o3">o3</option>
                    <option value="o3-mini">o3-mini</option>
                    <option value="o4-mini">o4-mini</option>
                    <option value="gpt-realtime">gpt-realtime</option>
                    <option value="gpt-4o-realtime-preview">gpt-4o-realtime-preview</option>
                    <option value="gpt-4o-mini-realtime-preview">gpt-4o-mini-realtime-preview</option>
                    <option value="gpt-realtime-mini">gpt-realtime-mini</option>
                    <option value="gpt-realtime-1.5">gpt-realtime-1.5</option>
                    <option value="gpt-realtime-2">gpt-realtime-2</option>
                    <option value="gpt-realtime-2.1">gpt-realtime-2.1</option>
                    <option value="gpt-realtime-2.1-mini">gpt-realtime-2.1-mini</option>
                    <!-- Diese Option wird bisher nicht im Phonebot umgesetzt und wurde deshalb auskommentiert. -->
                    <!-- <option value="mistral-mistral-small">mistral-mistral-small</option> -->
                    <!-- <option value="mistral-mistral-large">mistral-mistral-large</option> -->
                  </Field>
                  <ErrorMessage name="chatmodel" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>STT Model</label>
                  <Field as="select" class="form-control" v-model="vbot.sttmodel" name="sttmodel" id="sttmodel">
                    <option value="OpenAI" selected>OpenAI</option>
                    <option value="Microsoft">Microsoft</option>
                    <!-- Diese Option wird bisher nicht im Phonebot umgesetzt und wurde deshalb auskommentiert. -->
                    <!-- <option value="Voxtral">Voxtral</option> -->
                    <!-- <option value="mistral-voxtral-mini">mistral-voxtral-mini</option> -->
                  </Field>
                  <ErrorMessage name="sttmodel" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TTS Model</label>
                  <Field as="select" class="form-control" v-model="vbot.ttsmodel" name="ttsmodel" id="ttsmodel">
                    <option value="OpenAI" selected>OpenAI</option>
                    <option value="Microsoft">Microsoft</option>
                    <!-- Diese Option wird bisher nicht im Phonebot umgesetzt und wurde deshalb auskommentiert. -->
                    <!-- <option value="mistral-voxtral-tts">mistral-voxtral-tts</option> -->
                  </Field>
                  <ErrorMessage name="ttsmodel" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Voice für OpenAI</label>
                  <Field as="select" class="form-control" v-model="vbot.voice" name="voice" id="voice">
                    <option value="shimmer">Shimmer</option>
                    <option value="ash">Ash</option>
                    <option value="echo">Echo</option>
                    <option value="ballad">Ballad</option>
                    <option value="verse">Verse</option>
                    <option value="sage">Sage</option>
                    <option value="coral">Coral</option>
                    <option value="marin">Marin</option>
                    <option value="cedar">Cedar</option>
                  </Field>
                  <ErrorMessage name="voice" class="text-danger" />
                </div>
              </div>
            </div>

            <!-- Azure Voice Live (nur Superadmin), nur wenn oben Azure als Realtime-Anbieter gewählt ist.
                 Feldnamen bei AI-Phone: euDataProcessing, azure... -->
            <template v-if="role === 'super'">
              <div class="border rounded p-3 mb-4" v-if="azureActive">
                <h6 class="mb-3">Azure Voice Live</h6>

                <div class="row">
                  <div class="col-md-6">
                    <div class="input-group input-group-static mb-4">
                      <label class="form-check-label" for="eu_data_processing">
                        EU-Datenverarbeitung
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>EU-Datenverarbeitung</strong>
                            AI-Phone spricht bei allen KI-Anfragen nur EU-Server an. Der Realtime-Anbieter bleibt dann fest auf Azure.
                          </div>
                        </span>
                      </label>
                      <div class="form-check">
                        <input
                          type="checkbox"
                          class="form-check-input"
                          v-model="vbot.eu_data_processing"
                          name="eu_data_processing"
                          id="eu_data_processing"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Ohne Ressource und API-Key lehnt das Backend das Speichern ab -->
                <div class="alert alert-warning" v-if="azureMissingFields.length">
                  Für Azure fehlt noch: {{ azureMissingFields.join(', ') }}. Ohne diese Angaben lässt sich der Phonebot nicht speichern.
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        Azure-Ressource
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>Azure-Ressource</strong>
                            Name der Ressource wie im Azure-Portal, ohne https:// und ohne Domain.
                          </div>
                        </span>
                      </label>
                      <Field type="text" class="form-control" v-model="vbot.azure_voice_live_resource" name="azure_voice_live_resource" id="azure_voice_live_resource" />
                      <small class="form-text text-warning w-100" v-if="azureResourceLooksLikeUrl">
                        Nur den Namen der Ressource eintragen, keine URL (das Backend lehnt sie ab).
                      </small>
                      <ErrorMessage name="azure_voice_live_resource" class="text-danger" />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        Azure-API-Key
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>Azure-API-Key</strong>
                            Schlüssel 1 der Ressource. Wird verschlüsselt in der Datenbank gespeichert und an den Phonebot übermittelt. Feld leeren entfernt den Key.
                          </div>
                        </span>
                      </label>
                      <!-- Ein gespeicherter Key kommt als ******** zurück; beim Anklicken markieren, damit ein neuer Key ihn ersetzt -->
                      <Field type="password" class="form-control" v-model="vbot.azure_voice_live_api_key" name="azure_voice_live_api_key" id="azure_voice_live_api_key" autocomplete="new-password" @focus="$event.target.select()" />
                      <ErrorMessage name="azure_voice_live_api_key" class="text-danger" />
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        Chat-Deployment
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>Chat-Deployment</strong>
                            Name des Modell-Deployments in Microsoft Foundry. Bei Validate Transfer erzeugt AI-Phone damit außerhalb der Realtime-Session die Nachricht an die Zielperson bzw. den Betreff beim E-Mail-Versand.
                          </div>
                        </span>
                      </label>
                      <Field type="text" class="form-control" v-model="vbot.azure_chat_deployment" name="azure_chat_deployment" id="azure_chat_deployment" placeholder="z. B. gpt-4.1-mini" />
                      <small class="form-text text-warning w-100" v-if="!vbot.azure_chat_deployment">
                        Ohne Chat-Deployment kann AI-Phone mit Azure weder die Nachricht an die Zielperson (Validate Transfer) noch den E-Mail-Betreff erzeugen.
                      </small>
                      <ErrorMessage name="azure_chat_deployment" class="text-danger" />
                    </div>
                  </div>
                </div>

                <h6 class="mb-3">Optional</h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        Azure-Stimme
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>Azure-Stimme</strong>
                            Voice-ID von Azure. Leer = die Stimme aus „Voice für OpenAI“ (oben).
                          </div>
                        </span>
                      </label>
                      <Field type="text" class="form-control" v-model="vbot.azure_voice_live_voice" name="azure_voice_live_voice" id="azure_voice_live_voice" :placeholder="'leer = ' + (vbot.voice || 'Voice für OpenAI')" />
                      <ErrorMessage name="azure_voice_live_voice" class="text-danger" />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        Azure-Realtime-Modell
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>Azure-Realtime-Modell</strong>
                            Leer = AI-Phone leitet es aus dem Chat Model ab: gpt-realtime, gpt-realtime-1.5 und gpt-realtime-2.1 bekommen ihre -datazone-Variante, alle anderen gpt-realtime-2.1-datazone.
                          </div>
                        </span>
                      </label>
                      <Field type="text" class="form-control" v-model="vbot.azure_voice_live_model" name="azure_voice_live_model" id="azure_voice_live_model" list="azure-voice-live-models" :placeholder="'leer = ' + azureDefaultModel" />
                      <datalist id="azure-voice-live-models">
                        <option v-for="model in azureVoiceLiveModels" :key="model" :value="model"></option>
                      </datalist>
                      <ErrorMessage name="azure_voice_live_model" class="text-danger" />
                    </div>
                  </div>
                </div>

                <h6 class="mb-1">Feineinstellungen</h6>
                <p class="text-muted small mb-3">Die Standardwerte von AI-Phone passen normalerweise, die Felder also leer lassen.</p>
                <div class="row">
                  <div class="col-md-4">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        Transkriptionsmodell
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>Transkriptionsmodell</strong>
                            Modell, das in der Azure-Session mitschreibt, z. B. azure-speech oder gpt-4o-transcribe. Leer = Standard von AI-Phone.
                          </div>
                        </span>
                      </label>
                      <Field type="text" class="form-control" v-model="vbot.azure_voice_live_transcription_model" name="azure_voice_live_transcription_model" id="azure_voice_live_transcription_model" placeholder="leer = Standard" />
                      <ErrorMessage name="azure_voice_live_transcription_model" class="text-danger" />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        Rauschunterdrückung
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>Rauschunterdrückung</strong>
                            Einstellung der Rauschunterdrückung in der Azure-Session. Leer = Standard von AI-Phone.
                          </div>
                        </span>
                      </label>
                      <Field type="text" class="form-control" v-model="vbot.azure_voice_live_noise_suppression" name="azure_voice_live_noise_suppression" id="azure_voice_live_noise_suppression" placeholder="leer = Standard" />
                      <ErrorMessage name="azure_voice_live_noise_suppression" class="text-danger" />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="input-group input-group-static mb-4">
                      <label>
                        API-Version
                        <span class="info-icon">
                          i
                          <div class="info-tooltip">
                            <strong>API-Version</strong>
                            Version der Voice-Live-API im Format JJJJ-MM-TT, ggf. mit -preview, z. B. 2026-07-15. Leer = Standard von AI-Phone.
                          </div>
                        </span>
                      </label>
                      <Field type="text" class="form-control" v-model="vbot.azure_voice_live_api_version" name="azure_voice_live_api_version" id="azure_voice_live_api_version" placeholder="leer = Standard" />
                      <ErrorMessage name="azure_voice_live_api_version" class="text-danger" />
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>Open AI Key</label>
                  <Field type="text" class="form-control" v-model="vbot.apen_ai_key" name="apen_ai_key" id="apen_ai_key" />
                  <ErrorMessage name="apen_ai_key" class="text-danger" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>Mistral Key</label>
                  <Field type="text" class="form-control" v-model="vbot.mistral_api_key" name="mistral_api_key" id="mistral_api_key" />
                  <ErrorMessage name="mistral_api_key" class="text-danger" />
                </div>
              </div>
            </div>
            <div class="row">
            <div class="col-md-6">
              <div class="input-group input-group-static mb-4">
                <label>
                  Temperatur
                  <span class="info-icon">
                    i
                    <div class="info-tooltip">
                      <strong>KI-Kreativität</strong>
                      0.1-0.3: Konsistent | 0.5-0.8: Ausgewogen | 1.0+: Kreativ<br>
                      <strong>Bereich:</strong> Realtime: 0.6-1.2 | Standard: 0.0-2.0
                    </div>
                  </span>
                </label>
                <Field
                  type="number"
                  class="form-control"
                  v-model="vbot.temperature"
                  name="temperature"
                  id="temperature"
                  :min="temperatureMin"
                  :max="temperatureMax"
                  :step="0.1"
                />
                <small class="form-text text-muted">
                  Erlaubter Bereich: {{ temperatureMin }} - {{ temperatureMax }}
                </small>
                <ErrorMessage name="temperature" class="text-danger" />
              </div>
            </div>
            <!-- Effort / Reasoning-Aufwand (nur Superadmin) -->
            <div class="col-md-6" v-if="role === 'super'">
              <div class="input-group input-group-static mb-4">
                <label>
                  Effort
                  <span class="info-icon">
                    i
                    <div class="info-tooltip">
                      <strong>Reasoning Effort</strong>
                      Steuert den Reasoning-Aufwand des Chat-Modells.<br>
                      "default" überlässt die Wahl dem Modell. (Nur Superadmin)
                    </div>
                  </span>
                </label>
                <Field as="select" class="form-control" v-model="vbot.effort" name="effort" id="effort">
                  <option value="default">default</option>
                  <option value="minimal">minimal</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="xhigh">xhigh</option>
                </Field>
                <ErrorMessage name="effort" class="text-danger" />
              </div>
            </div>
          </div>

            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="forwarding_through_openai_tool">Forwarding Through OpenAI Tool</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.forwarding_through_openai_tool"
                      name="forwarding_through_openai_tool"
                      id="forwarding_through_openai_tool"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="websearch_openai">Activate Websearch in OpenAI</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.websearch_openai"
                      name="websearch_openai"
                      id="websearch_openai"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- MCP Server (falls aktiviert) -->
            <div class="row" v-if="vbot.forwarding_through_openai_tool === true">
              <div class="col-md-12">
                <label>MCP Server</label>
                <div v-for="(server, index) in vbot.mcp_servers" :key="index" class="d-flex mb-2 align-items-center">
                  <input v-model="server.url" class="form-control me-2" placeholder="Server URL" />
                  <button type="button" class="btn btn-danger btn-sm" @click="removeMcpServer(index)" v-if="vbot.mcp_servers.length > 0">
                    <i class="fas fa-minus"></i>
                  </button>
                </div>
                <button type="button" class="btn btn-primary btn-sm mt-2" @click="addMcpServer">
                  <i class="fas fa-plus"></i> MCP Server hinzufügen
                </button>
              </div>
            </div>

            <!-- MCP Authorization Header (nur Superadmin, wenn ein MCP-Server angegeben ist) -->
            <div class="row mt-3" v-if="role === 'super' && vbot.forwarding_through_openai_tool === true && hasMcpServer">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>
                    MCP Authorization Header
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>MCP Authorization Header</strong>
                        Bearer Token für die Authentifizierung gegenüber den MCP-Servern. Wird verschlüsselt in der Datenbank gespeichert und an den Phonebot übermittelt.
                      </div>
                    </span>
                  </label>
                  <Field type="password" class="form-control" v-model="vbot.mcp_authorization_header" name="mcp_authorization_header" id="mcp_authorization_header" autocomplete="new-password" />
                  <ErrorMessage name="mcp_authorization_header" class="text-danger" />
                </div>
              </div>
            </div>

            <!-- Realtime API Einstellungen -->
            <div class="row" v-if="vbot.realtime_api === true">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Redegeschwindigkeit</label>
                  <Field type="number" class="form-control" v-model="vbot.speed_of_speech" name="speed_of_speech" id="speed_of_speech" />
                  <ErrorMessage name="speed_of_speech" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="row" v-if="vbot.realtime_api === true">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>Transcription of Language</label>
                  <Field as="select" class="form-control" v-model="vbot.transcription_language" name="transcription_language" id="transcription_language">
                    <option value="de">de</option>
                    <option value="en">en</option>
                    <option value="fr">fr</option>
                    <option value="it">it</option>
                    <option value="all">all</option>
                  </Field>
                  <ErrorMessage name="transcription_language" class="text-danger" />
                </div>
              </div>
            </div>
          </div>

          <!-- DOLMETSCHER TAB (nur Superadmin): Übersetztes Gespräch / Live-Dolmetscher nach der Doku von AI-Phone.
               Feldnamen bei AI-Phone: translationMode, translation... -->
          <div v-if="activeTab === 'translation' && role === 'super'" class="tab-pane active">
            <h5 class="mb-3">🌍 Übersetztes Gespräch (Live-Dolmetscher)</h5>
            <p class="text-muted small mb-4">
              Ruft jemand an, der kein Deutsch spricht, verbindet AI-Phone ihn selbst mit dem Mitarbeiter, bleibt in der Leitung
              und übersetzt live in beide Richtungen. Jeder hört nur die Übersetzung des anderen. Ohne Modus ändert sich nichts.
            </p>

            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Modus
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Modus</strong>
                        auto: übersetzen, wenn der Anrufer eine andere Sprache spricht.<br>
                        ondemand: nie von selbst, der Mitarbeiter schaltet per Taste zu.<br>
                        always: jede Weiterleitung übersetzen (zum Testen).
                      </div>
                    </span>
                  </label>
                  <Field as="select" class="form-control" v-model="vbot.translation_mode" name="translation_mode" id="translation_mode">
                    <option value="">Aus (Standard, alles wie bisher)</option>
                    <option value="auto">auto – bei fremdsprachigen Anrufern</option>
                    <option value="ondemand">ondemand – nur per Taste des Mitarbeiters</option>
                    <option value="always">always – jede Weiterleitung (zum Testen)</option>
                  </Field>
                  <ErrorMessage name="translation_mode" class="text-danger" />
                </div>
              </div>
            </div>

            <template v-if="translationActive">
              <!-- Voraussetzungen laut AI-Phone; gespeichert wird trotzdem -->
              <div class="alert alert-warning" v-if="translationRequirementIssues.length">
                <strong>Noch nicht bereit für die Übersetzung:</strong>
                <ul class="mb-0 ps-3">
                  <li v-for="issue in translationRequirementIssues" :key="issue">{{ issue }}</li>
                </ul>
              </div>

              <div class="border rounded p-3 mb-4 small text-muted">
                <strong>Kosten und Leitungen:</strong> Pro Gespräch laufen zwei Übersetzungs-Sitzungen bei OpenAI über den OpenAI-Key
                dieses Phonebots, für die ganze Dauer. Jedes Gespräch belegt zwei Leitungen der Nebenstelle; die Telefonanlage muss zwei
                gleichzeitige Gespräche erlauben. Der Mitarbeiter sieht die Nummer von AI-Phone, nicht die des Anrufers.
                <span v-if="vbot.translation_mode === 'ondemand'">
                  Bei ondemand verbindet AI-Phone jede Weiterleitung selbst, auch die deutschsprachigen.
                </span>
              </div>

              <h6 class="mb-3">Sprachen</h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="input-group input-group-static mb-4">
                    <label>
                      Sprache der Mitarbeiter
                      <span class="info-icon">
                        i
                        <div class="info-tooltip">
                          <strong>Sprache der Mitarbeiter</strong>
                          Sprachcode nach ISO 639, z. B. de. Angaben wie de-DE werden zu de.
                        </div>
                      </span>
                    </label>
                    <Field type="text" class="form-control" v-model="vbot.translation_staff_language" name="translation_staff_language" id="translation_staff_language" list="translation-languages" placeholder="leer = de" />
                    <ErrorMessage name="translation_staff_language" class="text-danger" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="input-group input-group-static mb-4">
                    <label>
                      Sprache des Anrufers
                      <span class="info-icon">
                        i
                        <div class="info-tooltip">
                          <strong>Sprache des Anrufers</strong>
                          Gilt bei always und ondemand, wenn die KI keine andere Sprache als die der Mitarbeiter meldet.
                        </div>
                      </span>
                    </label>
                    <Field type="text" class="form-control" v-model="vbot.translation_caller_language" name="translation_caller_language" id="translation_caller_language" list="translation-languages" placeholder="leer = en" />
                    <ErrorMessage name="translation_caller_language" class="text-danger" />
                  </div>
                </div>
              </div>
              <div class="row" v-if="vbot.translation_mode === 'auto'">
                <div class="col-md-6">
                  <div class="input-group input-group-static mb-4">
                    <label>
                      Nicht übersetzen
                      <span class="info-icon">
                        i
                        <div class="info-tooltip">
                          <strong>Nicht übersetzen</strong>
                          Sprachen, die die Mitarbeiter selbst sprechen. Solche Anrufer werden bei auto normal weitergeleitet.
                        </div>
                      </span>
                    </label>
                    <Field type="text" class="form-control" v-model="vbot.translation_skip_languages" name="translation_skip_languages" id="translation_skip_languages" placeholder="z. B. en, fr" />
                    <small class="form-text text-muted w-100">Sprachcodes, durch Komma getrennt.</small>
                    <ErrorMessage name="translation_skip_languages" class="text-danger" />
                  </div>
                </div>
              </div>
              <datalist id="translation-languages">
                <option v-for="language in translationLanguages" :key="language.code" :value="language.code">{{ language.name }}</option>
              </datalist>

              <h6 class="mb-3">Ansage für den Mitarbeiter</h6>
              <div class="row">
                <div class="col-md-12">
                  <div class="input-group input-group-static mb-4">
                    <label>
                      Ansage
                      <span class="info-icon">
                        i
                        <div class="info-tooltip">
                          <strong>Ansage für den Mitarbeiter</strong>
                          Hört der Mitarbeiter nach dem Abnehmen, genau so vorgelesen. Der Anrufer wartet, solange sie läuft.
                          „off“ = keine Ansage, sofort verbinden.
                        </div>
                      </span>
                    </label>
                    <Field type="text" class="form-control" v-model="vbot.translation_staff_notice" name="translation_staff_notice" id="translation_staff_notice" maxlength="300" placeholder="leer = Hinweis: Dieses Gespräch wird live übersetzt." />
                    <small class="form-text text-muted w-100" v-if="vbot.translation_mode === 'ondemand'">
                      Bei ondemand beginnt das Gespräch ohne diesen Hinweis. Ist Validate Transfer an, hört der Mitarbeiter dessen Ansage.
                    </small>
                    <small class="form-text text-muted w-100" v-else>
                      Ersetzt bei auto und always die Ansage von Validate Transfer. Die Standard-Ansage nennt keine Tasten.
                    </small>
                    <ErrorMessage name="translation_staff_notice" class="text-danger" />
                  </div>
                </div>
              </div>

              <h6 class="mb-1">Tasten des Mitarbeiters</h6>
              <p class="text-muted small mb-3">
                Wirken erst nach der Ansage, Tasten des Anrufers bewirken nichts. Die Telefonanlage muss Tastendrücke als eigene
                Ereignisse übertragen (RFC 4733, telephone-event).
              </p>
              <div class="row">
                <div class="col-md-6">
                  <div class="input-group input-group-static mb-4">
                    <label>
                      Umschalt-Taste
                      <span class="info-icon">
                        i
                        <div class="info-tooltip">
                          <strong>Übersetzung ab- und zuschalten</strong>
                          Ein Ton beim Abschalten, zwei beim Zuschalten. Abgeschaltet hören sich beide direkt, es fallen keine Übersetzungskosten an.
                        </div>
                      </span>
                    </label>
                    <Field as="select" class="form-control" v-model="vbot.translation_toggle_key" name="translation_toggle_key" id="translation_toggle_key">
                      <option value="">{{ translationToggleDefaultLabel }}</option>
                      <option v-for="key in translationKeys" :key="key" :value="key">{{ key }}</option>
                      <option value="off">off (keine Taste)</option>
                    </Field>
                    <ErrorMessage name="translation_toggle_key" class="text-danger" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="input-group input-group-static mb-4">
                    <label>
                      Übergabe-Taste
                      <span class="info-icon">
                        i
                        <div class="info-tooltip">
                          <strong>An die Telefonanlage übergeben</strong>
                          Die Anlage verbindet Anrufer und Mitarbeiter direkt, AI-Phone steigt aus (drei Töne). Lässt sich nicht zurücknehmen.
                        </div>
                      </span>
                    </label>
                    <Field as="select" class="form-control" v-model="vbot.translation_handover_key" name="translation_handover_key" id="translation_handover_key">
                      <option value="">keine (Standard)</option>
                      <option v-for="key in translationKeys" :key="key" :value="key">{{ key }}</option>
                    </Field>
                    <ErrorMessage name="translation_handover_key" class="text-danger" />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <label class="d-flex align-items-center gap-2">
                    Sprachwahl per Taste
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Sprache per Taste wählen</strong>
                        Umschalt-Taste und innerhalb von vier Sekunden die Ziffer, z. B. *2. Der Mitarbeiter hört den Namen der Sprache.
                        Ohne Namen nennt AI-Phone die Sprache selbst.
                      </div>
                    </span>
                  </label>
                  <div v-for="(entry, index) in vbot.translation_language_keys" :key="index" class="d-flex align-items-center mb-2 gap-2">
                    <select class="form-control translation-key-select" v-model="entry.key" :aria-label="'Ziffer der Sprach-Taste ' + (index + 1)">
                      <option value="" disabled>Ziffer</option>
                      <option v-for="digit in translationDigits" :key="digit" :value="digit">{{ digit }}</option>
                    </select>
                    <input class="form-control" v-model="entry.language" list="translation-languages" placeholder="Sprachcode, z. B. tr" :aria-label="'Sprache der Sprach-Taste ' + (index + 1)" />
                    <input class="form-control" v-model="entry.name" maxlength="50" placeholder="Name (optional), z. B. Türkisch" :aria-label="'Name der Sprache der Sprach-Taste ' + (index + 1)" />
                    <button type="button" class="btn btn-danger btn-sm mb-0" @click="removeTranslationLanguageKey(index)" title="Sprach-Taste entfernen">
                      <i class="fas fa-minus"></i>
                    </button>
                  </div>
                  <button type="button" class="btn btn-primary btn-sm mt-2" @click="addTranslationLanguageKey" v-if="vbot.translation_language_keys.length < 10">
                    <i class="fas fa-plus"></i> Sprach-Taste hinzufügen
                  </button>
                </div>
              </div>

              <!-- Überschneidungen und unvollständige Zeilen; die meisten lehnt auch das Backend ab -->
              <div class="alert alert-warning mt-3" v-if="translationKeyIssues.length">
                <ul class="mb-0 ps-3">
                  <li v-for="issue in translationKeyIssues" :key="issue">{{ issue }}</li>
                </ul>
              </div>

              <h6 class="mb-1 mt-4">Feineinstellung</h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="input-group input-group-static mb-4">
                    <label>
                      Übersetzungsmodell
                      <span class="info-icon">
                        i
                        <div class="info-tooltip">
                          <strong>Übersetzungsmodell</strong>
                          Modell von OpenAI für die Übersetzung. Normalerweise nicht ändern.
                        </div>
                      </span>
                    </label>
                    <Field type="text" class="form-control" v-model="vbot.translation_model" name="translation_model" id="translation_model" placeholder="leer = gpt-realtime-translate" />
                    <ErrorMessage name="translation_model" class="text-danger" />
                  </div>
                </div>
              </div>

              <!-- Getesteter Prompt-Abschnitt aus der Doku von AI-Phone (Stand 13.10.2026) -->
              <details class="mb-4">
                <summary class="fw-bold">Prompt-Abschnitt für die Sprache</summary>
                <p class="text-muted small mt-2 mb-2">
                  So getestet: Im Prompt darf nicht stehen, dass die KI immer Deutsch spricht oder „sofort“ anrufen soll.
                  Den Beispielsatz in der ersten Zeile bewusst als Beispiel lassen, mit der Anweisung „kündige die Verbindung an“ sprach die KI im Test genau diese Anweisung aus.
                  <span v-if="vbot.translation_mode === 'ondemand'">
                    Bei ondemand fehlt die Zeile zur Übersetzung, weil die KI sonst eine Übersetzung ankündigt, die nicht kommt.
                  </span>
                  <span v-if="vbot.translation_staff_language && vbot.translation_staff_language !== 'de'">
                    Der Abschnitt geht von deutschsprachigen Mitarbeitern aus und muss für „{{ vbot.translation_staff_language }}“ angepasst werden.
                  </span>
                </p>
                <pre class="translation-prompt">{{ translationPromptSnippet }}</pre>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="copyTranslationPrompt">
                  <i class="fas fa-copy me-1"></i>
                  Kopieren
                </button>
              </details>
            </template>
          </div>

          <!-- NETWORK TAB -->
          <div v-if="activeTab === 'network'" class="tab-pane active">
            <h5 class="mb-3">🌐 Netzwerk-Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Transport Type</label>
                  <Field as="select" class="form-control" v-model="vbot.transmit_type" name="transmit_type" id="transmit_type">
                    <option value="" disabled>Select TransportType</option>
                    <option value="tcp">tcp</option>
                    <option value="udp">udp</option>
                    <option value="tls">tls</option>
                    <option value="stcp">stcp</option>
                  </Field>
                  <ErrorMessage name="transmit_type" class="text-danger" />
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>STUN Server</label>
                  <Field type="text" class="form-control" v-model="vbot.stun_server" name="stun_server" id="stun_server" />
                  <ErrorMessage name="stun_server" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TURN Server</label>
                  <Field type="text" class="form-control" v-model="vbot.turnserver" name="turnserver" id="turnserver" />
                  <ErrorMessage name="turnserver" class="text-danger" />
                </div>
              </div>
               <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Outbound Proxy</label>
                  <Field type="text" class="form-control" v-model="vbot.OutboundProxy" name="OutboundProxy" id="OutboundProxy" />
                  <ErrorMessage name="OutboundProxy" class="text-danger" />
                </div>
              </div>
              
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TURN Server Username</label>
                  <Field type="text" class="form-control" v-model="vbot.nat_username" name="nat_username" id="nat_username" />
                  <ErrorMessage name="nat_username" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TURN Server Password</label>
                  <Field type="text" class="form-control" v-model="vbot.nat_password" name="nat_password" id="nat_password" />
                  <ErrorMessage name="nat_password" class="text-danger" />
                </div>
              </div>
             
            </div>
            <div class="row">       
            <div class="col-md-4">
              <div class="input-group input-group-static mb-4">
                <label class="form-check-label" for="useSRTP">
                  Use SRTP
                  <span class="info-icon">
                    i
                    <div class="info-tooltip">
                      <strong>Verschlüsselung</strong>
                      SRTP verschlüsselt Sprachdaten.
                    </div>
                  </span>
                </label>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" v-model="vbot.useSRTP" name="useSRTP" id="useSRTP" />
                </div>
              </div>
            </div>
          </div>
          </div>

          <!-- ADVANCED TAB -->
          <div v-if="activeTab === 'advanced'" class="tab-pane active">
            <h5 class="mb-3">🔧 Erweiterte Einstellungen</h5>

            
            

            <!-- URLs -->
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>PromptXT-URL</label>
                  <Field type="text" class="form-control" v-model="vbot.prompturl" name="prompturl" id="prompturl" />
                  <ErrorMessage name="prompturl" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Outbound List URL</label>
                  <Field type="text" class="form-control" v-model="vbot.outbound_list_url" name="outbound_list_url" id="outbound_list_url" />
                  <ErrorMessage name="outbound_list_url" class="text-danger" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Flowise URL</label>
                  <Field type="text" class="form-control" v-model="vbot.flowiseurl" name="flowiseurl" id="flowiseurl" />
                  <ErrorMessage name="flowiseurl" class="text-danger" />
                </div>
              </div>
            <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>n8n URL</label>
                  <Field type="text" class="form-control" v-model="vbot.n8n_url" name="n8n_url" id="n8n_url" />
                  <ErrorMessage name="n8n_url" class="text-danger" />
                </div>
              </div>
            </div>
            
            <!-- Web Log Checkbox -->
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="web_log_enabled">Web-Log</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.web_log_enabled"
                      name="web_log_enabled"
                      id="web_log_enabled"
                    />
                  </div>
                </div>
              </div>
            </div>


            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="attendedtransfer">Attended Transfer</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.attendedtransfer"
                      name="attendedtransfer"
                      id="attendedtransfer"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6" v-show="vbot.attendedtransfer">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="validatetransfer">Validate Transfer</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.validatetransfer"
                      name="validatetransfer"
                      id="validatetransfer"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- Nebenstellen-Status, bei denen nicht weitergeleitet wird (nur Superadmin) -->
            <div class="row" v-if="role === 'super'">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label class="w-100">
                    Nicht weiterleiten bei Nebenstellen-Status
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Unreachable Extension States</strong>
                        Meldet die Telefonanlage für die Ziel-Nebenstelle einen der ausgewählten Status, hört der Anrufer sofort „nicht erreichbar“ und wird nicht weitergeleitet.<br>
                        Vorher im Log („ExtensionState …“) prüfen, ob die Anlage den Status zuverlässig meldet. (Nur Superadmin)
                      </div>
                    </span>
                  </label>
                  <div class="form-check form-check-inline ps-0 me-4" v-for="state in extensionStates" :key="state">
                    <label class="form-check-label">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="vbot.unreachable_extension_states"
                        :value="state"
                        name="unreachable_extension_states"
                      />
                      {{ state }}
                    </label>
                  </div>
                  <small class="form-text text-muted w-100">
                    Ohne Auswahl wird der Status nur geloggt und wie bisher weitergeleitet.
                  </small>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Time until call abort (seconds)</label>
                  <Field type="number" class="form-control" v-model="vbot.abort_time" name="abort_time" id="abort_time" />
                  <ErrorMessage name="abort_time" class="text-danger" />
                </div>
              </div>

            </div>
            <!-- Category Terms -->
            <div class="row">
              <div class="col-md-12">
                <div class="mb-4">
                  <label class="form-label d-block">Category Terms (Max 5)</label>
                  <div v-for="(term, index) in vbot.category_terms" :key="index" class="d-flex align-items-center mb-2">
                    <div class="flex-grow-1 input-group input-group-static">
                      <Field
                        type="text"
                        class="form-control border-0 border-bottom"
                        v-model="vbot.category_terms[index]"
                        :name="'category_terms_' + index"
                        :id="'category_terms_' + index"
                      />
                    </div>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-danger ms-2" 
                      @click="deleteCategoryTerm(index)"
                      v-if="vbot.category_terms.length > 1"
                    >
                      <i class="fas fa-minus"></i>
                    </button>
                  </div>
                  <ErrorMessage name="category_terms" class="text-danger" />
                  <button
                    v-if="vbot.category_terms.length < 5"
                    type="button"
                    class="btn btn-sm btn-primary mt-2"
                    @click="addCategoryTerm"
                  >
                    <i class="fas fa-plus"></i> Add Term
                  </button>
                  <div v-else>
                    <small class="text-muted">Maximum 5 category terms allowed.</small>
                  </div>
                </div>
              </div>
            </div>

            
          </div>


        <!-- Hidden Fields -->
        <Field type="hidden" class="form-control" v-model="vbot.id" name="id" id="id" />

        <!-- Submit Button -->
        <div class="button-row d-flex justify-content-end mt-4">
          <button class="btn btn-dark" type="submit">
            Update Phonebot
          </button>
        </div>
      </div>
    </Form>
  </div>
</div>
</template>

<script>
import { Form, Field, ErrorMessage } from "vee-validate";
import showSwal from "@/mixins/showSwal";
import Swal from 'sweetalert2';
import userService from "@/services/users.service";

export default {
  name: "Info4",
  
  components: {
    Form,
    Field,
    ErrorMessage
  },
  
  data() {
    return {
      // Benutzerrolle (steuert rollenabhängige Felder, z.B. SIP-Domain)
      role: '',

      // Tab-Steuerung
      activeTab: 'general',

      // Nebenstellen-Status, die der Phonebot kennt (identisch zu Vbot::EXTENSION_STATES im Backend)
      extensionStates: ['Available', 'Ringing', 'Busy'],

      // Vorschläge für das Azure-Realtime-Modell: die EU-Varianten (Data Zone), die AI-Phone kennt
      azureVoiceLiveModels: ['gpt-realtime-2.1-datazone', 'gpt-realtime-1.5-datazone', 'gpt-realtime-datazone'],

      // Live-Dolmetscher: Tasten für Umschalten und Übergabe (wie TRANSLATION_KEY_REGEX im VbotController), Sprach-Tasten nur Ziffern
      translationKeys: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'],
      translationDigits: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],

      // Vorschläge für die Sprachcodes des Live-Dolmetschers (ISO 639), andere Codes lassen sich frei eintragen
      translationLanguages: [
        { code: 'de', name: 'Deutsch' },
        { code: 'en', name: 'Englisch' },
        { code: 'tr', name: 'Türkisch' },
        { code: 'uk', name: 'Ukrainisch' },
        { code: 'ru', name: 'Russisch' },
        { code: 'ar', name: 'Arabisch' },
        { code: 'pl', name: 'Polnisch' },
        { code: 'ro', name: 'Rumänisch' },
        { code: 'bg', name: 'Bulgarisch' },
        { code: 'hr', name: 'Kroatisch' },
        { code: 'sr', name: 'Serbisch' },
        { code: 'el', name: 'Griechisch' },
        { code: 'it', name: 'Italienisch' },
        { code: 'fr', name: 'Französisch' },
        { code: 'es', name: 'Spanisch' },
        { code: 'pt', name: 'Portugiesisch' },
        { code: 'nl', name: 'Niederländisch' },
        { code: 'fa', name: 'Persisch' },
        { code: 'vi', name: 'Vietnamesisch' },
        { code: 'zh', name: 'Chinesisch' },
      ],

      // Enhanced Overlay-Steuerung
      showPromptOverlay: false,
      showAiMode: false,
      originalPrompt: '',
      generatedPrompt: 'generierter Prompt Text',
      isGenerating: false,
      
      // VBot-Daten
      vbot: {
        // Basic Info
        id: '',
        bezeichnung: '',
        product_name: '',
        license_Key: '',
        payment_status: 0,
        
        // Content
        ansagetext: '',
        prompt: '',
        
        // SIP Settings
        sip_benutzername: '',
        sip_passwd: '',
        sip_registername: '',
        sip_phonenumber: '',
        sip_server: '',
        sip_domain: '',
        sip_port: '',
        transmit_type: '',
        attendedtransfer: false,
        validatetransfer: false,
        unreachable_extension_states: [], // Nebenstellen-Status, bei denen nicht weitergeleitet wird (nur Superadmin)
        useSRTP: false,
        sdk: '',
        abort_time: '',
        
        // Email Settings
        e_mail_server: '',
        e_mail_user: '',
        e_mail_passwd: '',
        e_mail_server_port: '',
        e_mail_server_from: '',
        mail_certifikate_validation: false,
        
        // AI Models
        chatmodel: "gpt-4o",
        sttmodel: "OpenAI",
        ttsmodel: "OpenAI",
        voice: "Shimmer",
        effort: "default", // Reasoning-Aufwand (nur Superadmin)
        apen_ai_key: '',
        mistral_api_key: '',
        
        // Network
        prompturl: '',
        stun_server: '',
        turnserver: '',
        nat_username: '',
        nat_password: '',
        OutboundProxy: '',
        outbound_list_url: '',
        
        // Advanced
        web_log_enabled: true,
        category_terms: [],
        flowiseurl: '',
        n8n_url: '',
        
        // AI Features
        forwarding_through_openai_tool: false,
        websearch_openai: false,
        mcp_servers: [{ url: '' }],
        mcp_authorization_header: '',

        // Realtime API
        realtime_api: false,
        temperature: '',
        speed_of_speech: '',
        transcription_language: 'de',

        // EU-Datenverarbeitung und Azure Voice Live (nur Superadmin)
        eu_data_processing: false,
        realtime_provider: '',
        azure_voice_live_resource: '',
        azure_voice_live_api_key: '',
        azure_chat_deployment: '',
        azure_voice_live_voice: '',
        azure_voice_live_model: '',
        azure_voice_live_transcription_model: '',
        azure_voice_live_noise_suppression: '',
        azure_voice_live_api_version: '',

        // Übersetztes Gespräch / Live-Dolmetscher (nur Superadmin), leer = Standard von AI-Phone
        translation_mode: '',
        translation_staff_language: '',
        translation_caller_language: '',
        translation_skip_languages: '',
        translation_staff_notice: '',
        translation_toggle_key: '',
        translation_handover_key: '',
        translation_language_keys: [],
        translation_model: ''
      }
    };
  },
  
  computed: {
    // Admin-Rollen (identisch zu updateAdminFields im VbotController) - steuert Felder wie die SIP-Domain
    isAdminRole() {
      return ['admin', 'adminuser', 'super'].includes(this.role);
    },

    // Mindestens ein MCP-Server mit URL angegeben (steuert Sichtbarkeit des MCP Authorization Headers)
    hasMcpServer() {
      return Array.isArray(this.vbot.mcp_servers) &&
        this.vbot.mcp_servers.some(server => server.url && server.url.trim() !== '');
    },

    // Azure wird genutzt: mit EU-Datenverarbeitung immer, sonst wenn als Realtime-Anbieter gewählt
    azureActive() {
      return Boolean(this.vbot.eu_data_processing) || this.vbot.realtime_provider === 'azure';
    },

    // Pflichtangaben für Azure, die noch fehlen (ohne sie lehnt das Backend das Speichern ab)
    azureMissingFields() {
      if (!this.azureActive) {
        return [];
      }
      return [
        !this.vbot.azure_voice_live_resource && 'Azure-Ressource',
        !this.vbot.azure_voice_live_api_key && 'Azure-API-Key',
      ].filter(Boolean);
    },

    // Häufiger Fehler: Endpunkt-URL statt Ressourcenname eingetragen
    azureResourceLooksLikeUrl() {
      return /[./:]/.test(this.vbot.azure_voice_live_resource || '');
    },

    // Modell, das AI-Phone ohne eigenen Eintrag aus dem Chat Model ableitet (Vorgabe des AI-Phone-Entwicklers)
    azureDefaultModel() {
      const datazoneModels = {
        'gpt-realtime': 'gpt-realtime-datazone',
        'gpt-realtime-1.5': 'gpt-realtime-1.5-datazone',
        'gpt-realtime-2.1': 'gpt-realtime-2.1-datazone',
      };
      return datazoneModels[this.vbot.chatmodel] || 'gpt-realtime-2.1-datazone';
    },

    // Live-Dolmetscher eingeschaltet (auto, ondemand oder always)
    translationActive() {
      return ['auto', 'ondemand', 'always'].includes(this.vbot.translation_mode);
    },

    // Voraussetzungen laut Doku von AI-Phone (Abschnitte 2 und 6), die noch fehlen
    translationRequirementIssues() {
      if (!this.translationActive) {
        return [];
      }
      return [
        this.vbot.eu_data_processing && 'EU-Datenverarbeitung ist an (Reiter KI-Modelle): Damit ist die Übersetzung gesperrt. AI-Phone bekommt den Modus „off“ und leitet normal weiter.',
        !this.vbot.forwarding_through_openai_tool && '„Forwarding Through OpenAI Tool“ ist aus (Reiter KI-Modelle): Die KI meldet die Sprache des Anrufers über das Werkzeug call.',
        !this.vbot.apen_ai_key && 'Kein OpenAI-Key (Reiter KI-Modelle): Die Übersetzung läuft über OpenAI.',
        this.vbot.chatmodel !== 'gpt-realtime-2.1' && 'Chat Model ist nicht gpt-realtime-2.1 (Reiter KI-Modelle): Nur damit antwortete die KI im Test zuverlässig in der Sprache des Anrufers.',
        !['all', '', null, undefined].includes(this.vbot.transcription_language) && 'Transcription of Language steht nicht auf „all“ (Reiter KI-Modelle): Sonst steht fremdsprachiger Text falsch im Gesprächsprotokoll.',
        this.vbot.realtime_provider === 'azure' && !this.vbot.eu_data_processing && 'Mit Azure als Realtime-Anbieter ist die Übersetzung noch nicht getestet.',
      ].filter(Boolean);
    },

    // Ziffern der Sprach-Tasten (auch aus noch unvollständigen Zeilen)
    translationLanguageKeyDigits() {
      return (this.vbot.translation_language_keys || []).map(entry => entry.key).filter(Boolean);
    },

    // Umschalt-Taste, die bei AI-Phone gilt (wie Vbot::effectiveTranslationToggleKey im Backend)
    translationEffectiveToggleKey() {
      const key = this.vbot.translation_toggle_key;
      if (key === 'off') {
        return null;
      }
      if (key) {
        return key;
      }
      return this.vbot.translation_mode === 'ondemand' || this.translationLanguageKeyDigits.length ? '*' : null;
    },

    // Beschriftung für "leer" bei der Umschalt-Taste: Standard von AI-Phone
    translationToggleDefaultLabel() {
      return this.vbot.translation_mode === 'ondemand' || this.translationLanguageKeyDigits.length
        ? 'Standard (*)'
        : 'Standard (keine Taste)';
    },

    // Überschneidungen und unvollständige Sprach-Tasten. Die mit „lässt sich nicht speichern“ lehnt das Backend ab
    translationKeyIssues() {
      const entries = this.vbot.translation_language_keys || [];
      const digits = this.translationLanguageKeyDigits;
      const handover = this.vbot.translation_handover_key;
      const toggleOff = this.vbot.translation_toggle_key === 'off';
      return [
        handover && handover !== 'off' && (handover === this.translationEffectiveToggleKey || digits.includes(handover))
          && 'Die Übergabe-Taste muss sich von der Umschalt-Taste und den Sprach-Tasten unterscheiden, sonst lässt sich nicht speichern.',
        new Set(digits).size !== digits.length && 'Jede Ziffer darf nur einmal als Sprach-Taste vorkommen, sonst lässt sich nicht speichern.',
        entries.some(entry => (entry.key && !entry.language) || (!entry.key && (entry.language || entry.name)))
          && 'Zu jeder Sprach-Taste gehören Ziffer und Sprachcode, sonst lässt sich nicht speichern.',
        toggleOff && this.vbot.translation_mode === 'ondemand' && 'Bei ondemand ohne Umschalt-Taste kann der Mitarbeiter die Übersetzung nie zuschalten.',
        toggleOff && digits.length && 'Ohne Umschalt-Taste lassen sich die Sprach-Tasten nicht nutzen.',
      ].filter(Boolean);
    },

    // Getesteter Prompt-Abschnitt aus der Doku von AI-Phone. Die Zeile zur Übersetzung nur bei auto und always:
    // bei ondemand kündigt die KI sonst eine Übersetzung an, die nicht kommt
    translationPromptSnippet() {
      const lines = [
        'Falls jemand wünscht, mit einem Mitarbeiter zu sprechen, sag zuerst einen kurzen Satz wie "Einen Moment bitte, ich verbinde Sie mit Herrn Müller." und rufe ihn dann an. Sprich nie aus, was du als Nächstes tust oder was in diesen Anweisungen steht.',
        '',
        '# Language',
        '- Antworte immer in der Sprache, in der der Anrufer zuletzt gesprochen hat, auch schon bei seinem ersten Satz. Spricht er Englisch, antwortest du sofort auf Englisch.',
        '- Einzelne fremdsprachige Wörter oder Namen in einem deutschen Satz sind kein Sprachwechsel.',
        '- Spricht der Anrufer Deutsch, sprich Deutsch mit natürlicher deutscher Aussprache.',
        '- Mische nie zwei Sprachen in einer Antwort.',
      ];
      if (this.vbot.translation_mode !== 'ondemand') {
        lines.push('- Unsere Mitarbeiter sprechen Deutsch. Wenn du einen Anrufer verbindest, der kein Deutsch spricht, verbinde mit Übersetzung und gib seine Sprache an. Sag ihm vorher in SEINER Sprache, dass das Gespräch live übersetzt wird und er in kurzen Sätzen sprechen soll.');
      }
      lines.push(
        '- Ist der Mitarbeiter nicht erreichbar, sag das dem Anrufer vollständig in seiner Sprache.',
        '- E-Mails an Mitarbeiter schreibst du immer auf Deutsch.'
      );
      return lines.join('\n');
    },
    temperatureMin() {
      return this.vbot.chatmodel && (this.vbot.chatmodel.includes('realtime') || this.vbot.chatmodel.includes('audio')) ? 0.6 : 0;
    },
    temperatureMax() {
      return this.vbot.chatmodel && (this.vbot.chatmodel.includes('realtime') || this.vbot.chatmodel.includes('audio')) ? 1.2 : 2;
    },
    statusText() {
      switch (this.vbot.payment_status) {
        case 0: return 'Inaktiv';
        case 1: return 'Aktiv';
        case 2: return 'Pausiert';
        case 3: return 'Zahlungsrückstand';
        case 4: return 'Gekündigt';
        default: return 'Unbekannt';
      }
    },
    statusClass() {
      switch (this.vbot.payment_status) {
        case 0: return 'bg-secondary text-white';
        case 1: return 'bg-success text-white';
        case 2: return 'bg-warning text-dark';
        case 3: return 'bg-danger text-white';
        case 4: return 'bg-dark text-white';
        default: return 'bg-secondary text-white';
      }
    },
  },

  watch: {
    // Mit EU-Datenverarbeitung nutzt AI-Phone immer Azure; das Backend speichert es genauso
    'vbot.eu_data_processing'(enabled) {
      if (enabled) {
        this.vbot.realtime_provider = 'azure';
      }
    },

    // Wie das Backend beim Speichern (updateAdminFields): Realtime-API genau dann, wenn das Chat Model "realtime" enthält.
    // So erscheinen Redegeschwindigkeit und Transcription of Language gleich nach dem Wechsel (für den Dolmetscher nötig)
    'vbot.chatmodel'(model) {
      this.vbot.realtime_api = String(model || '').includes('realtime');
    },
  },

  methods: {

    // Benutzerrolle laden (steuert Sichtbarkeit der rollenabhängigen Felder)
    async fetchRole() {
      try {
        const response = await userService.fetchRole();
        this.role = response.role;
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    },

    // Tab-Steuerung
    setActiveTab(tabName) {
      this.activeTab = tabName;
    },

    // Enhanced Prompt Overlay Methoden
    openPromptOverlay() {
      this.originalPrompt = this.vbot.prompt;
      this.showPromptOverlay = true;
      this.showAiMode = false;
      this.$nextTick(() => {
        this.$refs.fullscreenPrompt?.focus();
        document.body.style.overflow = 'hidden';
      });
    },

    closePromptOverlay() {
      this.showPromptOverlay = false;
      this.showAiMode = false;
      document.body.style.overflow = '';
    },

    saveAndClose() {
      this.closePromptOverlay();
    },

    handleEscape(event) {
      if (event.key === 'Escape' && this.showPromptOverlay) {
        this.closePromptOverlay();
      }
    },

    // AI-Modus Methoden
    toggleAiMode() {
      this.showAiMode = !this.showAiMode;
      if (this.showAiMode && !this.generatedPrompt) {
        this.generatedPrompt = 'generierter Prompt Text';
      }
    },

    async generatePrompt() {

  // ✅ FALLBACK: Verwende license_Key wenn id fehlt
  let vbotId = this.vbot.id;
  
  if (!vbotId) {
    console.warn('⚠️ VBot ID fehlt, verwende license_Key als Fallback');
    vbotId = this.vbot.license_Key || this.$route.params.license_Key;
  }

  if (!vbotId) {
    showSwal.methods.showSwal({
      type: "error",
      message: "Fehler: VBot-ID nicht verfügbar. Bitte laden Sie die Seite neu.",
      width: 500,
      timer: 4000
    });
    return;
  }

  this.isGenerating = true;
  
  try {
    const promptData = {
      current_prompt: this.vbot.prompt || ''
    };

    const response = await this.$store.dispatch('vbot/generatePrompt', {
      vbotId: vbotId,  // ✅ Verwende die validierte ID
      promptData: promptData
    });

    this.generatedPrompt = response.generated_prompt;
    
    showSwal.methods.showSwal({
      type: "success",
      message: "Prompt erfolgreich generiert!",
      width: 400,
      timer: 2000
    });
    
  } catch (error) {
    console.error('❌ Prompt generation error:', error);
    
    let errorMessage = "Fehler bei der Prompt-Generierung";
    if (error.response?.status === 404) {
      errorMessage = `API-Route nicht gefunden. VBot-ID: ${vbotId}`;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }
    
    showSwal.methods.showSwal({
      type: "error",
      message: errorMessage,
      width: 500,
      timer: 5000
    });
  } finally {
    this.isGenerating = false;
  }
},

    copyGeneratedPrompt() {
      if (navigator.clipboard && this.generatedPrompt) {
        navigator.clipboard.writeText(this.generatedPrompt).then(() => {
          showSwal.methods.showSwal({
            type: "success",
            message: "Generierter Prompt kopiert!",
            width: 400,
            timer: 2000
          });
        });
      }
    },

    async applyGeneratedPrompt() {
      if (this.generatedPrompt) {
        const result = await Swal.fire({
          title: 'Prompt übernehmen?',
          html: `
            <div class="text-start">
              <p class="mb-3"><strong>Sie sind dabei, den generierten Prompt zu übernehmen.</strong></p>
              <div class="alert alert-warning text-start mb-0">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Der Prompt wird erst <strong>nach dem Klick auf "Update Phonebot"</strong> 
                endgültig gespeichert.
              </div>
            </div>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Ja, übernehmen',
          cancelButtonText: 'Abbrechen',
        });

        if (result.isConfirmed) {
          this.vbot.prompt = this.generatedPrompt;
          showSwal.methods.showSwal({
            type: "success",
            message: "Prompt übernommen!",
            width: 450,
            timer: 4000
          });
        }
      }
    },

    // Bestehende Methoden
    async handleUpdate() {
      try {
        const payload = {
          ...this.vbot,
          attendedtransfer: Boolean(this.vbot.attendedtransfer),
          validatetransfer: Boolean(this.vbot.validatetransfer),
          // Ganz leere Zeilen der Sprach-Tasten nicht mitschicken
          translation_language_keys: (this.vbot.translation_language_keys || []).filter(entry => entry.key || entry.language || entry.name),
        };
        await this.$store.dispatch('vbot/updatePhonebot', payload);
        showSwal.methods.showSwal({
          type: "success",
          message: "Phonebot Updated Successfully",
          width: 500,
          timer: 160000,
          showConfirmButton: true
        });
        this.$router.push({ name: 'phonebots' });
      } catch (error) {
        console.error('Phonebot error:', error);
        // Validierungsfehler des Backends (z. B. fehlende Azure-Angaben) im Klartext zeigen
        const details = (error.response?.data?.errors || []).map(e => e.detail).filter(Boolean);
        if (error.response?.status === 422 && details.length) {
          Swal.fire({ icon: 'error', title: 'Speichern nicht möglich', text: details.join(' ') });
          return;
        }
        showSwal.methods.showSwal({
          type: "error",
          message: "Error In Updating Phonebot!",
          width: 500
        });
      }
    },

    async deleteItem(vid) {
      const result = await Swal.fire({
        title: 'Sind Sie sicher?',
        text: "Diese Aktion kann nicht rückgängig gemacht werden!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ja, löschen!',
        cancelButtonText: 'Abbrechen'
      });

      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('vbot/deleteVbot', vid);
          Swal.fire('Gelöscht!', 'Der Phonebot wurde erfolgreich gelöscht.', 'success');
          this.$router.push({ name: 'phonebots' });
        } catch (error) {
          console.error(`Error deleting phonebot with ID: ${vid}`, error);
          Swal.fire('Fehler!', 'Beim Löschen ist ein Fehler aufgetreten.', 'error');
        }
      }
    },

    addCategoryTerm() {
      if (this.vbot.category_terms.length < 5) {
        this.vbot.category_terms.push("");
      }
    },

    deleteCategoryTerm(index) {
      if (this.vbot.category_terms.length > 1) {
        this.vbot.category_terms.splice(index, 1);
      }
    },

    addMcpServer() {
      this.vbot.mcp_servers.push({ url: '' });
    },

    removeMcpServer(idx) {
      if (this.vbot.mcp_servers.length > 0) {
        this.vbot.mcp_servers.splice(idx, 1);
      }
    },

    // Sprach-Tasten des Live-Dolmetschers (höchstens 10, eine je Ziffer)
    addTranslationLanguageKey() {
      if (this.vbot.translation_language_keys.length < 10) {
        this.vbot.translation_language_keys.push({ key: '', language: '', name: '' });
      }
    },

    removeTranslationLanguageKey(index) {
      this.vbot.translation_language_keys.splice(index, 1);
    },

    copyTranslationPrompt() {
      // Ohne Zwischenablage (z. B. Seite über http statt https) oder bei verweigertem Zugriff einen Hinweis zeigen
      const failed = () => showSwal.methods.showSwal({
        type: "error",
        message: "Kopieren nicht möglich. Bitte den Text markieren und mit Strg+C kopieren.",
        width: 450,
        timer: 4000
      });
      if (!navigator.clipboard) {
        failed();
        return;
      }
      navigator.clipboard.writeText(this.translationPromptSnippet).then(() => {
        showSwal.methods.showSwal({
          type: "success",
          message: "Prompt-Abschnitt kopiert!",
          width: 400,
          timer: 2000
        });
      }).catch(failed);
    },

    openCheckout() {
      if (window.Paddle) {
        window.Paddle.Checkout.open({
          items: [{
            priceId: 'pri_01k367k95yg378z5gk5jbf6fwq',
            quantity: 1,
          }],
          customData: {
            license_key: this.vbot.license_Key
          },
        });
      }
    }
  },

  async mounted() {
    // Event Listener
    document.addEventListener('keydown', this.handleEscape);

    // Rolle laden, damit rollenabhängige Felder (z.B. SIP-Domain) korrekt ein-/ausgeblendet werden
    await this.fetchRole();

    const licenseKey = this.$route.params.license_Key;

    // Paddle-Script laden
    const script = document.createElement("script");
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.onload = () => {
      if (window.Paddle) {
        window.Paddle.Environment.set("sandbox");
        window.Paddle.Initialize({
          token: "test_29aed94bafd8aa545b89c19df4f",
          checkout: {
            settings: {
              displayMode: "inline",
              frameTarget: "checkout-container",
              frameInitialHeight: "450",
              frameStyle: "width: 100%; min-width: 312px; background-color: transparent; border: none;"
            }
          }
        });
      }
    };
    document.body.appendChild(script);

    // VBot Daten laden
    try {
      const vbots = await this.$store.dispatch('vbot/getVbotById', licenseKey);
      this.vbot = {
        ...vbots,
        mail_certifikate_validation: vbots.mail_certifikate_validation === "1" || vbots.mail_certifikate_validation === 1,
        attendedtransfer: vbots.attendedtransfer === true || vbots.attendedtransfer === "1" || vbots.attendedtransfer === 1,
        validatetransfer: vbots.validatetransfer === true || vbots.validatetransfer === "1" || vbots.validatetransfer === 1,
        unreachable_extension_states: Array.isArray(vbots.unreachable_extension_states) ? vbots.unreachable_extension_states : [],
        useSRTP: vbots.useSRTP === "1" || vbots.useSRTP === 1,
        realtime_api: vbots.realtime_api === "1" || vbots.realtime_api === 1,
        web_log_enabled: vbots.web_log_enabled === "1" || vbots.web_log_enabled === 1,
        forwarding_through_openai_tool: vbots.forwarding_through_openai_tool === "1" || vbots.forwarding_through_openai_tool === 1,
        websearch_openai: vbots.websearch_openai === "1" || vbots.websearch_openai === 1,
        category_terms: Array.isArray(vbots.category_terms) ? vbots.category_terms : [],
        mcp_servers: Array.isArray(vbots.mcp_servers) && vbots.mcp_servers.length > 0 ? vbots.mcp_servers : [],
        sttmodel: vbots.sttmodel || "OpenAI",
        ttsmodel: vbots.ttsmodel || "OpenAI",
        chatmodel: vbots.chatmodel || "gpt-4o",
        effort: vbots.effort || "default",
        voice: vbots.voice ? vbots.voice.toLowerCase() : "shimmer",
        eu_data_processing: [true, 1, "1"].includes(vbots.eu_data_processing),
        realtime_provider: vbots.realtime_provider || '',
        // Live-Dolmetscher: "off" und leer bedeuten dasselbe, die Auswahl kennt nur leer
        translation_mode: vbots.translation_mode && vbots.translation_mode !== 'off' ? vbots.translation_mode : '',
        translation_toggle_key: vbots.translation_toggle_key || '',
        translation_handover_key: vbots.translation_handover_key && vbots.translation_handover_key !== 'off' ? vbots.translation_handover_key : '',
        translation_language_keys: Array.isArray(vbots.translation_language_keys)
          ? vbots.translation_language_keys.map(entry => ({ key: String(entry.key ?? ''), language: entry.language || '', name: entry.name || '' }))
          : []
      };
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscape);
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
/* ✅ HEADER STYLING */
.phonebot-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.phonebot-header h4 {
  color: #2c3e50;
  font-weight: 600;
}

.phonebot-header .text-muted {
  font-size: 0.9rem;
}

.header-actions .btn {
  font-weight: 500;
  padding: 8px 16px;
}

/* ✅ SICHERSTELLUNG DASS SWEETALERT2 ÜBER PROMPT-OVERLAY ERSCHEINT */
:deep(.swal-high-z-index) {
  z-index: 10001 !important;
}

/* ✅ GLOBAL SWEETALERT2 Z-INDEX FIX */
:global(.swal2-container) {
  z-index: 10001 !important;
}

:global(.swal2-backdrop-show) {
  z-index: 10001 !important;
}

/* Weißer Hintergrund für alle Input-Felder */
.form-control {
  background-color: #ffffff !important;
}

/* Weißer Hintergrund für Textareas */
textarea.form-control {
  background-color: #ffffff !important;
}

/* Weißer Hintergrund für Select-Felder */
select.form-control {
  background-color: #ffffff !important;
}

/* Weißer Hintergrund für den gesamten Tab-Content-Bereich */
.tab-content {
  background-color: #ffffff;
}

.tab-pane {
  background-color: #ffffff;
  padding: 20px 0;
}

.input-group-static label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #17a2b8;
  color: white;
  font-size: 11px;
  font-weight: bold;
  cursor: help;
  position: relative;
  transition: all 0.3s;
}

.info-icon:hover {
  background: #138496;
  transform: scale(1.1);
}

.info-tooltip {
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background: #2c3e50;
  color: white;
  padding: 10px 15px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: normal;
  white-space: normal;
  width: 250px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  line-height: 1.4;
}

.info-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #2c3e50;
}

.info-icon:hover .info-tooltip {
  opacity: 1;
  visibility: visible;
  bottom: 135%;
}

.info-tooltip strong {
  display: block;
  margin-bottom: 5px;
  color: #3498db;
}

/* Tab Navigation */
.nav-tabs .nav-link {
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
  color: #007bff;
  background-color: #f8f9fa;
}

.nav-tabs .nav-link.active {
  font-weight: 600;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  color: #007bff;
}

/* Prompt Trigger Styles */
.prompt-trigger {
  transition: all 0.3s ease;
  border: 2px dashed #dee2e6 !important;
}

.prompt-trigger:hover {
  background-color: #f8f9fa !important;
  border-color: #007bff !important;
  transform: scale(1.01);
}

.prompt-trigger:focus {
  background-color: #f8f9fa !important;
  border-color: #007bff !important;
}

/* ✅ ENHANCED PROMPT EDITOR STYLES */

/* Backdrop - absolut im Vordergrund */
.prompt-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  backdrop-filter: blur(3px);
}

/* Hauptcontainer - über allem */
.prompt-fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

/* Editor Container */
.prompt-fullscreen-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1400px; /* Breiter für Dual-View */
  height: 85vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  animation: slideInScale 0.4s ease;
  overflow: hidden;
}

/* Header */
.prompt-header {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.prompt-header .header-actions {
  gap: 8px;
}

/* Content Bereich */
.prompt-content {
  flex: 1;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Single View (Normal) */
.single-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Dual View (AI-Modus) */
.dual-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.dual-view .row {
  flex: 1;
  margin: 0;
}

.prompt-column {
  padding: 0 10px;
  height: 100%;
}

.prompt-column:first-child {
  border-right: 2px solid #e9ecef;
  padding-right: 15px;
}

.prompt-column:last-child {
  padding-left: 15px;
}

/* Prompt Sections */
.prompt-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.prompt-section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e9ecef;
}

.prompt-section-textarea {
  flex: 1;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
  outline: none;
  transition: border-color 0.3s ease;
  min-height: 300px;
}

.prompt-section-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Generated Prompt Specific */
.generated-prompt {
  background-color: #f8f9fa !important;
  border-color: #28a745 !important;
}

.generated-prompt:focus {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1) !important;
}

/* Regular Textarea (Single View) */
.prompt-fullscreen-textarea {
  flex: 1;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
  outline: none;
  transition: border-color 0.3s ease;
}

.prompt-fullscreen-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Footer */
.prompt-footer {
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

/* Abo-Info Card Styles */
.card {
  border-radius: 8px;
}

.card-header {
  border-radius: 8px 8px 0 0;
}

/* Animationen */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInScale {
  from { 
    opacity: 0;
    transform: scale(0.9) translateY(-30px);
  }
  to { 
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .phonebot-header {
    padding: 15px;
  }
  
  .phonebot-header .d-flex {
    flex-direction: column;
    gap: 15px;
    align-items: stretch !important;
  }
  
  .prompt-fullscreen-overlay {
    padding: 10px;
  }
  
  .prompt-fullscreen-container {
    height: 95vh;
    max-height: none;
    max-width: none;
  }
  
  .prompt-header {
    padding: 12px 15px;
  }
  
  .prompt-content {
    padding: 15px;
  }
  
  .prompt-footer {
    padding: 12px 15px;
  }
  
  /* Mobile: Stack columns vertically */
  .dual-view .prompt-column {
    padding: 0;
  }
  
  .dual-view .prompt-column:first-child {
    border-right: none;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 15px;
    margin-bottom: 15px;
  }
  
  .dual-view .prompt-column:last-child {
    padding-left: 0;
  }
}

/* Scrollbar für Textarea */
.prompt-fullscreen-textarea::-webkit-scrollbar,
.prompt-section-textarea::-webkit-scrollbar {
  width: 8px;
}

.prompt-fullscreen-textarea::-webkit-scrollbar-track,
.prompt-section-textarea::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.prompt-fullscreen-textarea::-webkit-scrollbar-thumb,
.prompt-section-textarea::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.prompt-fullscreen-textarea::-webkit-scrollbar-thumb:hover,
.prompt-section-textarea::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* ✅ CUSTOM MODAL STYLES */
:global(.apply-prompt-modal) {
  border-radius: 12px !important;
}

:global(.apply-prompt-modal .swal2-html-container) {
  margin: 0 !important;
  padding: 0 !important;
}

:global(.apply-prompt-modal .alert) {
  border-radius: 8px;
  font-size: 0.9rem;
}

:global(.apply-prompt-modal .swal2-actions) {
  gap: 10px;
}

:global(.apply-prompt-modal .swal2-confirm) {
  padding: 10px 20px !important;
  font-weight: 500 !important;
}

:global(.apply-prompt-modal .swal2-cancel) {
  padding: 10px 20px !important;
  font-weight: 500 !important;
}

/* Live-Dolmetscher: Ziffer der Sprach-Taste schmal, Prompt-Abschnitt mit Zeilenumbrüchen */
.translation-key-select {
  max-width: 110px;
}

.translation-prompt {
  white-space: pre-wrap;
  font-size: 0.8rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
  max-height: 320px;
  overflow-y: auto;
}
</style>